import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { EnterpriseEditionFeature } from '@/app/constants';
import { useRootStore } from '@n8n/stores/useRootStore';
import { useSettingsStore } from '@/app/stores/settings.store';
import * as externalSecretsApi from '@n8n/rest-api-client';
import { connectProvider } from '@n8n/rest-api-client';
import { useRBACStore } from '@/app/stores/rbac.store';
import { useProjectsStore } from '@/features/collaboration/projects/projects.store';
import type { ExternalSecretsProvider } from '@n8n/api-types';

/**
 * Transforms flat dot-notated secret keys into a nested object structure.
 *
 * Takes secrets grouped by provider (e.g., `{ "aws": ["db.host", "db.password", "api_key"] }`)
 * and converts them into nested objects with masked values (e.g., `{ "aws": { "db": { "host": "*********", "password": "*********" }, "api_key": "*********" } }`).
 *
 * @param secrets - Record of provider names to arrays of dot-notated secret keys
 * @returns Nested object structure with all values masked as '*********'
 */
function transformSecretsToNestedObject(
	secrets: Record<string, string[]>,
): Record<string, Record<string, object | string>> {
	return Object.keys(secrets).reduce<Record<string, Record<string, object | string>>>(
		(providerAcc, provider) => {
			const providerSecrets = secrets[provider];
			if (!Array.isArray(providerSecrets)) return providerAcc;
			providerAcc[provider] = providerSecrets.reduce<Record<string, object | string>>(
				(secretAcc, secret) => {
					const splitSecret = secret.split('.');
					if (splitSecret.length === 1) {
						secretAcc[secret] = '*********';
						return secretAcc;
					}
					const obj = secretAcc[splitSecret[0]] ?? {};
					let acc = obj;
					for (let i = 1; i < splitSecret.length; i++) {
						const key = splitSecret[i] as keyof typeof acc;
						// Actual value key
						if (i === splitSecret.length - 1) {
							acc[key] = '*********' as (typeof acc)[typeof key];
							continue;
						}
						if (Object.keys(acc) && !acc[key]) {
							acc[key] = {} as (typeof acc)[typeof key];
						}
						acc = acc[key];
					}
					secretAcc[splitSecret[0]] = obj;
					return secretAcc;
				},
				{},
			);

			return providerAcc;
		},
		{},
	);
}

export const useExternalSecretsStore = defineStore('externalSecrets', () => {
	const rootStore = useRootStore();
	const settingsStore = useSettingsStore();
	const rbacStore = useRBACStore();
	const projectsStore = useProjectsStore();

	const externalSecretsModuleSettings = computed(
		() => settingsStore.moduleSettings['external-secrets'],
	);

	const state = reactive({
		providers: [] as ExternalSecretsProvider[],
		// TODO: rename to globalSecrets once projectSecrets are live
		secrets: {} as Record<string, string[]>,
		projectSecrets: {} as Record<string, string[]>,
		connectionState: {} as Record<string, ExternalSecretsProvider['state']>,
	});

	const isEnterpriseExternalSecretsEnabled = computed(
		() => settingsStore.isEnterpriseFeatureEnabled[EnterpriseEditionFeature.ExternalSecrets],
	);

	// True if none of the new external secrets features are enabled.
	// Used to decide whether to fetch secrets via legacy/global methods rather than project-scoped APIs.
	const isLegacyMode = computed(
		() =>
			isEnterpriseExternalSecretsEnabled.value &&
			!externalSecretsModuleSettings.value?.roleBasedAccess &&
			!externalSecretsModuleSettings.value?.forProjects &&
			!externalSecretsModuleSettings.value?.multipleConnections,
	);

	const secrets = computed(() => state.secrets);
	const projectSecrets = computed(() => state.projectSecrets);
	const providers = computed(() => state.providers);
	const connectionState = computed(() => state.connectionState);

	const projectSecretsAsObject = computed(() =>
		transformSecretsToNestedObject(projectSecrets.value),
	);
	const globalSecretsAsObject = computed(() => transformSecretsToNestedObject(secrets.value));
	const secretsAsObject = computed(() => {
		if (externalSecretsModuleSettings.value?.forProjects) {
			/**
			 * This combines secrets from both global and project scopes.
			 * Note: The backend enforces that provider names are unique across scopes, preventing conflicts.
			 */
			return {
				...globalSecretsAsObject.value,
				...projectSecretsAsObject.value,
			};
		}
		return globalSecretsAsObject.value;
	});

	/**
	 * TODO: remove this once beta features (multiple connections and scoped access) are stable
	 */
	async function fetchLegacySecrets() {
		if (rbacStore.hasScope('externalSecret:list')) {
			try {
				state.secrets = await externalSecretsApi.getExternalSecrets(rootStore.restApiContext);
			} catch {
				state.secrets = {};
			}
		}
	}

	/**
	 * TODO: remove this once scoped access is stable
	 */
	async function fetchMultiConnectionsGlobalSecrets() {
		if (rbacStore.hasScope('externalSecret:list')) {
			try {
				state.secrets = await externalSecretsApi.getGlobalExternalSecrets(rootStore.restApiContext);
			} catch {
				state.secrets = {};
			}
		}
	}

	async function fetchScopedGlobalSecrets(projectId: string) {
		try {
			// There is no need to check for global scope permission
			// the backend has dedicated logic to return the global secrets
			// relevant to the project and the user
			state.secrets = await externalSecretsApi.getGlobalExternalSecretsForProject(
				rootStore.restApiContext,
				projectId,
			);
		} catch {
			state.secrets = {};
		}
	}

	async function fetchGlobalSecrets(projectId?: string) {
		// Community Edition does not register the external-secrets module, so
		// `moduleSettings['external-secrets']` is absent. Skip the fetch entirely
		// to avoid calling the EE-only endpoint (which 404s on CE). Everything
		// below assumes EE with the module loaded.
		const moduleConfig = externalSecretsModuleSettings.value;
		if (!moduleConfig) return;

		if (moduleConfig.roleBasedAccess) {
			// In principle when roleBasedAccess is enabled, projectId is always provided
			if (!projectId) {
				return;
			}

			await fetchScopedGlobalSecrets(projectId);
			return;
		}

		if (moduleConfig.forProjects || moduleConfig.multipleConnections) {
			await fetchMultiConnectionsGlobalSecrets();
			return;
		}

		await fetchLegacySecrets();
	}

	async function fetchProjectSecrets(projectId: string) {
		// Community Edition: external-secrets module is absent, so there is nothing to fetch.
		if (!externalSecretsModuleSettings.value) return;

		if (!externalSecretsModuleSettings.value.forProjects) {
			// project-scoped secrets are still under development. Only available behind feature flag
			return;
		}

		// It's not possible to scope secrets to a personal project,
		// therefore there is not need to fetch project secrets for a personal project.
		const isTeamProject = projectId !== projectsStore.personalProject?.id;
		if (!isTeamProject) {
			return;
		}

		const project =
			projectsStore.currentProject?.id === projectId
				? projectsStore.currentProject
				: projectsStore.myProjects.find((p) => p.id === projectId);
		if (
			rbacStore.hasScope('externalSecret:list') ||
			project?.scopes?.includes('externalSecret:list') === true
		) {
			try {
				state.projectSecrets = await externalSecretsApi.getProjectExternalSecrets(
					rootStore.restApiContext,
					projectId,
				);
			} catch {
				state.projectSecrets = {};
			}
		}
	}

	async function fetchSecretsForProject(projectId: string) {
		// Community Edition: external-secrets module is absent, so there is nothing to fetch.
		if (!externalSecretsModuleSettings.value) return;

		await Promise.all([fetchGlobalSecrets(projectId), fetchProjectSecrets(projectId)]);
	}

	async function reloadProvider(id: string) {
		const { updated } = await externalSecretsApi.reloadProvider(rootStore.restApiContext, id);
		if (updated && isLegacyMode.value) {
			await fetchGlobalSecrets();
		}

		return updated;
	}

	async function getProviders() {
		state.providers = await externalSecretsApi.getExternalSecretsProviders(
			rootStore.restApiContext,
		);
	}

	async function testProviderConnection(id: string, data: ExternalSecretsProvider['data']) {
		return await externalSecretsApi.testExternalSecretsProviderConnection(
			rootStore.restApiContext,
			id,
			data,
		);
	}

	async function getProvider(id: string) {
		const provider = await externalSecretsApi.getExternalSecretsProvider(
			rootStore.restApiContext,
			id,
		);

		const existingProviderIndex = state.providers.findIndex((p) => p.name === id);
		if (existingProviderIndex !== -1) {
			state.providers.splice(existingProviderIndex, 1, provider);
		} else {
			state.providers.push(provider);
		}

		return provider;
	}

	function updateStoredProvider(id: string, data: Partial<ExternalSecretsProvider>) {
		const providerIndex = state.providers.findIndex((p) => p.name === id);
		state.providers = [
			...state.providers.slice(0, providerIndex),
			{
				...state.providers[providerIndex],
				...data,
				data: {
					...state.providers[providerIndex].data,
					...data.data,
				},
			},
			...state.providers.slice(providerIndex + 1),
		];
	}

	async function updateProviderConnected(id: string, value: boolean) {
		await connectProvider(rootStore.restApiContext, id, value);
		if (isLegacyMode.value) {
			await fetchGlobalSecrets();
		}
		updateStoredProvider(id, { connected: value, state: value ? 'connected' : 'initializing' });
	}

	async function updateProvider(id: string, { data }: Partial<ExternalSecretsProvider>) {
		await externalSecretsApi.updateProvider(rootStore.restApiContext, id, data);
		if (isLegacyMode.value) {
			await fetchGlobalSecrets();
		}
		updateStoredProvider(id, { data });
	}

	function setConnectionState(id: string, connectionState: ExternalSecretsProvider['state']) {
		state.connectionState[id] = connectionState;
	}

	return {
		state,
		providers,
		secrets,
		connectionState,
		secretsAsObject,
		globalSecretsAsObject,
		projectSecretsAsObject,
		isEnterpriseExternalSecretsEnabled,
		fetchGlobalSecrets,
		fetchProjectSecrets,
		fetchSecretsForProject,
		getProvider,
		getProviders,
		testProviderConnection,
		updateProvider,
		updateStoredProvider,
		updateProviderConnected,
		reloadProvider,
		setConnectionState,
	};
});
